import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
} from '@headlessui/react';
import { User } from '@core/generated/gql-types';
import { actionPrimaryButtonClassName } from '@core/theme/actionTheme.util';

const CHANGE_REASON_MAX_LEN = 2000;

export interface AssignAdminConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (changeReason: string) => void;
  adminDisplayName: string;
  worker: User;
  isClearing: boolean;
  isSubmitting: boolean;
}

const AssignAdminConfirmModal: React.FC<AssignAdminConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  adminDisplayName,
  worker,
  isClearing,
  isSubmitting,
}) => {
  const [changeReason, setChangeReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setChangeReason('');
    }
  }, [isOpen]);

  const buttonLabel = () => {
    if (isSubmitting) {
      return isClearing ? 'Removing…' : 'Assigning…';
    }
    return isClearing ? 'Remove' : 'Assign';
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={() => !isSubmitting && onClose()}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
            >
              <DialogPanel className="flex max-w-md flex-col rounded-xl bg-white shadow-xl transition-all">
                <div className="border-b border-gray-200 px-4 py-3">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-medium text-gray-900"
                  >
                    {isClearing
                      ? 'Remove assigned admin'
                      : 'Confirm assigned admin'}
                  </DialogTitle>
                </div>
                <div className="space-y-4 px-4 py-4">
                  <p className="text-sm text-gray-700">
                    {isClearing ? (
                      <>
                        Are you sure you want to remove the assigned admin from{' '}
                        <span className="font-medium">{worker?.email}</span>?
                      </>
                    ) : (
                      <>
                        Are you sure you want to assign{' '}
                        <span className="font-medium">{adminDisplayName}</span>{' '}
                        to this user{' '}
                        <span className="font-medium">{worker?.email}</span>?
                      </>
                    )}
                  </p>
                  {worker.assignedAdminId && (
                    <div>
                      <label
                        htmlFor="assign-admin-change-reason"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Reason{' '}
                        <span className="font-normal text-gray-500">
                          (optional)
                        </span>
                      </label>
                      <textarea
                        id="assign-admin-change-reason"
                        rows={3}
                        maxLength={CHANGE_REASON_MAX_LEN}
                        value={changeReason}
                        onChange={(e) => setChangeReason(e.target.value)}
                        disabled={isSubmitting}
                        placeholder="Please explain why you are changing the assigned admin"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {changeReason.length}/{CHANGE_REASON_MAX_LEN}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={onClose}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => onConfirm(changeReason)}
                    className={`rounded-md px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${actionPrimaryButtonClassName}`}
                  >
                    {buttonLabel()}
                  </button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AssignAdminConfirmModal;
